import {
  ArrowLeftIcon,
  Avatar,
  Button,
  ButtonLink,
  Checkbox,
  Input,
  SearchIcon,
  UploadIcon,
  UserGroupIcon,
  WidgetSizeEnum,
} from '@wonderschool/common-base-ui';
import { ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExistingStudent } from './existingStudents';

interface SelectExistingStudentsProps {
  stepper?: ReactNode;
  students: ExistingStudent[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  onSelectAll: (select: boolean) => void;
  onImport: () => void;
  onBack: () => void;
  onContinue: () => void;
}

// Real students carry a resolved room name; fall back to a dash.
const roomLabel = (s: ExistingStudent) => s.roomName || '—';

/**
 * Step 1 of Add-to-billing. The families are already in CCMS, so there's
 * nothing to type: pick who to put on a recurring plan. Students already on an
 * active plan are shown but locked, so we never double-bill.
 */
export function SelectExistingStudents(p: SelectExistingStudentsProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return p.students;
    return p.students.filter((s) =>
      `${s.childFirstName} ${s.childLastName} ${s.parentEmail}`.toLowerCase().includes(q)
    );
  }, [p.students, query]);

  // Only not-yet-billed students are selectable; that's the pool "select all"
  // and the count refer to.
  const selectable = p.students.filter((s) => !s.alreadyBilled);
  const allSelected = selectable.length > 0 && p.selectedIds.length === selectable.length;

  // No students in CCMS yet: billing has nothing to work with, so the honest
  // next step is to import the roster first, not to set up billing.
  if (p.students.length === 0) {
    return (
      <section className="space-y-6 rounded-lg border border-gray-200 bg-white p-6" data-testid="atb-step-select-empty">
        {p.stepper && <div className="border-b border-gray-200 pb-4">{p.stepper}</div>}
        <div className="flex flex-col items-center gap-4 py-10 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-900/10">
            <UserGroupIcon className="h-8 w-8 text-blue-900" />
          </span>
          <div>
            <h3 className="text-xl font-bold text-navy-950">{t('Add your students first')}</h3>
            <p className="mx-auto mt-2 max-w-md text-sm text-gray-600">
              {t(
                "You don't have any students in Wonderschool yet. Import your roster or add them by hand, then come back to put them on billing."
              )}
            </p>
          </div>
          <Button primary preIcon={<UploadIcon className="size-5" />} onClick={p.onImport}>
            {t('Add students')}
          </Button>
        </div>
        <div className="flex items-center border-t border-gray-200 pt-4">
          <ButtonLink
            content={t('Go back')}
            iconDirection="left"
            icon={<ArrowLeftIcon className="size-4" />}
            onClick={p.onBack}
          />
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-6 rounded-lg border border-gray-200 bg-white p-6" data-testid="atb-step-select">
      {p.stepper && <div className="border-b border-gray-200 pb-4">{p.stepper}</div>}

      <div>
        <h2 className="text-2xl font-bold text-navy-950">{t('addToBilling.selection.title')}</h2>
        <p className="mt-1 text-sm text-gray-600">
          {t(
            "These are the students already in your account. Pick who you want to start invoicing, you'll set each one's tuition next. Anyone already on a plan is locked so they aren't billed twice."
          )}
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="w-full sm:w-80">
          <Input
            value={query}
            placeholder={t('Search by student or parent')}
            size={WidgetSizeEnum.SMALL}
            preIcon={<SearchIcon className="size-5 text-gray-800" />}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <button
          type="button"
          className="text-sm font-medium text-blue-900 hover:underline"
          onClick={() => p.onSelectAll(!allSelected)}
        >
          {allSelected ? t('Clear all') : t('Select all')}
        </button>
      </div>

      <div className="divide-y divide-gray-100 rounded-md border border-gray-200">
        {filtered.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-gray-500">
            {t('No students match "{{query}}".', { query })}
          </div>
        )}
        {filtered.map((s) => {
          const name = `${s.childFirstName} ${s.childLastName}`.trim();
          const checked = p.selectedIds.includes(s.id);
          return (
            <label
              key={s.id}
              className={`flex items-center gap-3 px-4 py-3 ${
                s.alreadyBilled ? 'cursor-default opacity-60' : 'cursor-pointer hover:bg-gray-50'
              }`}
            >
              <Checkbox
                checked={checked}
                disabled={s.alreadyBilled}
                onChange={() => p.onToggle(s.id)}
                data-testid={`atb-select-${s.id}`}
              />
              <Avatar displayName={name} size={WidgetSizeEnum.MEDIUM} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-gray-900">{name}</div>
                <div className="truncate text-xs text-gray-500">
                  {roomLabel(s)} · {s.parentEmail}
                </div>
              </div>
              {s.alreadyBilled && (
                <span className="flex-none rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-900">
                  {t('On billing')}
                </span>
              )}
            </label>
          );
        })}
      </div>

      <div className="flex items-center justify-between border-t border-gray-200 pt-4">
        <ButtonLink
          content={t('Go back')}
          iconDirection="left"
          icon={<ArrowLeftIcon className="size-4" />}
          onClick={p.onBack}
        />
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-600">{t('{{count}} selected', { count: p.selectedIds.length })}</span>
          <Button primary disabled={p.selectedIds.length === 0} onClick={p.onContinue}>
            {t('Continue to billing')} →
          </Button>
        </div>
      </div>
    </section>
  );
}
